import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  DialogActions,
} from '@mui/material';
import { useState, useEffect } from 'react';

import { useLazyQuery } from '@apollo/client/react';

import { graphql } from '@/gql';
import { Or2Button } from '@/components/button/Or2Button';
import { LoadableConfirmationButton } from '@/components/button/LoadableConfirmationButton';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import { Or2DateTimePicker } from '@/components/date-time/Or2DateTimePicker';

import { convertDataToCsv, downloadCsv } from './convertDataToCsv';

export const CSV_CONVERSATION_ITEM_FRAGMENT = graphql(`
  fragment csvConversationItem on OrtooGptConversationItem {
    _id
    response {
      responseText
      knowledgeSourcesConnection {
        edges {
          node {
            title
            url
          }
        }
      }
      intercomSourcesConnection {
        edges {
          node {
            title
            url
          }
        }
      }
    }
    prompt {
      text
    }
  }
`);

export const CSV_CONVERSATION_FRAGMENT = graphql(`
  fragment csvConversation on OrtooGptConversation {
    _id
    itemsConnection {
      edges {
        node {
          _id
          ...csvConversationItem
        }
      }
    }
  }
`);

const BULK_DOWNLOAD_CONVERSATIONS_QUERY = graphql(`
  query BulkDownloadOrtooGptConversations($timestamp: DateTime!) {
    getOrtooGptConversationsFromBulkUpload(timestamp: $timestamp) {
      _id
      ...csvConversation
    }
  }
`);

interface Props {
  open: boolean;
  onClose: () => void;
}

export const BulkDownloadDialog = ({ open, onClose }: Props) => {
  const [timestamp, setTimestamp] = useState<Date | null>(new Date());
  const [downloadRequested, setDownloadRequested] = useState(false);

  const [getConversations, { data, loading }] = useLazyQuery(
    BULK_DOWNLOAD_CONVERSATIONS_QUERY,
  );

  const handleDownload = async () => {
    setDownloadRequested(true);
    await getConversations({
      variables: {
        timestamp: (timestamp ?? new Date()).toISOString(),
      },
    });
  };

  useEffect(() => {
    const conversationItems = data?.getOrtooGptConversationsFromBulkUpload.map(
      (conversation) => extractConnectionProps(conversation.itemsConnection)[0],
    );

    if (downloadRequested && data) {
      const csv = convertDataToCsv(conversationItems ?? []);
      downloadCsv(csv, timestamp ?? new Date());
      setDownloadRequested(false);
    }
  }, [data, downloadRequested, timestamp]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk download</DialogTitle>
      <DialogContent>
        <Typography>
          This will download all conversations which were created from a bulk
          upload. Please ensure that you have waited for all responses to finish
          generating (ideally allow a minute).
        </Typography>
        <Typography fontWeight="bold">
          Please enter the time that you uploaded the conversations.
        </Typography>
        <Or2DateTimePicker
          value={timestamp}
          onChange={(value) => setTimestamp(value)}
          sx={{ marginTop: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Or2Button color="inherit" onClick={onClose}>
          Cancel
        </Or2Button>
        <LoadableConfirmationButton
          color="primary"
          onClick={handleDownload}
          disabled={loading}
        >
          Download
        </LoadableConfirmationButton>
      </DialogActions>
    </Dialog>
  );
};
